import { createClient } from '@supabase/supabase-js';
import type { Database } from './types';
import { brokeredPreviewStorage } from './previewAuthStorage';

// --- Production Supabase project (hardcoded) ---
const PROD_SUPABASE_URL = "https://jorctscopnxiptuunyev.supabase.co";
const PROD_SUPABASE_KEY = "eyJhbGciOiJIUzI1NiIsInR5cCI6IkpXVCJ9.eyJpc3MiOiJzdXBhYmFzZSIsInJlZiI6ImpvcmN0c2NvcG54aXB0dXVueWV2Iiwicm9sZSI6ImFub24iLCJpYXQiOjE3NzEzODY5MzYsImV4cCI6MjA4Njk2MjkzNn0.GRLdS6UlsmLkhrFKZo_Z3_nTCrwNAZASKyra76qDEdQ";

// --- DEV Supabase project (hardcoded fallback) ---
const DEV_SUPABASE_URL = "https://pdrvjjijsmmxtyngoplc.supabase.co";
const DEV_SUPABASE_KEY = "eyJhbGciOiJIUzI1NiIsInR5cCI6IkpXVCJ9.eyJpc3MiOiJzdXBhYmFzZSIsInJlZiI6InBkcnZqamlqc21teHR5bmdvcGxjIiwicm9sZSI6ImFub24iLCJpYXQiOjE3NTg3OTMzMTQsImV4cCI6MjA3NDM2OTMxNH0.iTXgkOSBT-SjU6JNdFKnQZuLkk0fIKfZZEJ6tHoFB_c";

function getSupabaseConfig(): { url: string; key: string } {
  const hostname = typeof window !== "undefined" ? window.location.hostname : "";
  const isPrivateNetworkHost =
    /^192\.168\./.test(hostname) ||
    /^10\./.test(hostname) ||
    /^172\.(1[6-9]|2\d|3[01])\./.test(hostname);
  const isDev =
    import.meta.env.DEV ||
    hostname === "localhost" ||
    hostname === "127.0.0.1" ||
    hostname.endsWith(".local") ||
    hostname.endsWith(".lovable.app") ||
    hostname.endsWith(".lovableproject.com") ||
    isPrivateNetworkHost;

  if (isDev) {
    return {
      url: import.meta.env.VITE_SUPABASE_URL || DEV_SUPABASE_URL,
      key: import.meta.env.VITE_SUPABASE_PUBLISHABLE_KEY || DEV_SUPABASE_KEY,
    };
  }

  // Everything else (custom domains, applyt.ai, etc.) → PROD
  return { url: PROD_SUPABASE_URL, key: PROD_SUPABASE_KEY };
}

const { url: SUPABASE_URL, key: SUPABASE_PUBLISHABLE_KEY } = getSupabaseConfig();
const SUPABASE_PROJECT_REF = new URL(SUPABASE_URL).hostname.split('.')[0] || "";
const SUPABASE_AUTH_STORAGE_KEY = SUPABASE_PROJECT_REF ? `sb-${SUPABASE_PROJECT_REF}-auth-token` : "";

// Import the supabase client like this:
// import { supabase } from "@/integrations/supabase/client";

export const clearSupabaseAuthStorage = () => {
  if (!SUPABASE_AUTH_STORAGE_KEY || typeof window === 'undefined') return;
  window.localStorage.removeItem(SUPABASE_AUTH_STORAGE_KEY);
};

const getRequestUrl = (input: RequestInfo | URL): string => {
  if (typeof input === 'string') return input;
  if (input instanceof URL) return input.href;
  return input.url;
};

export const isRefreshTokenRequest = (url: string) =>
  url.includes('/auth/v1/token') && url.includes('grant_type=refresh_token');

export const createSupabaseFetch = (baseFetch: typeof fetch) =>
  async (input: RequestInfo | URL, init?: RequestInit) => {
    const requestUrl = getRequestUrl(input);
    try {
      return await baseFetch(input, init);
    } catch (error) {
      if (isRefreshTokenRequest(requestUrl)) {
        console.warn('[Auth] Refresh token request failed; preserving local session state for retry.', error);
      }
      throw error;
    }
  };

const supabaseFetch = typeof fetch === 'function'
  ? createSupabaseFetch(fetch)
  : undefined;

const supabaseOptions: Parameters<typeof createClient>[2] = {
  auth: {
    storage: brokeredPreviewStorage(),
    persistSession: true,
    autoRefreshToken: true,
  }
};

if (supabaseFetch) {
  supabaseOptions.global = {
    ...(supabaseOptions.global ?? {}),
    fetch: supabaseFetch,
  };
}

export const supabase = createClient<Database>(SUPABASE_URL, SUPABASE_PUBLISHABLE_KEY, supabaseOptions);
export { SUPABASE_URL, SUPABASE_PUBLISHABLE_KEY, SUPABASE_AUTH_STORAGE_KEY };
